import React, { useState } from 'react';
import { getPhotoUrl, getPhotoUrlFromObject, Photo } from '../../api/photos';

interface AvatarProps {
  photo?: Photo | string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  shape?: 'circle' | 'square' | 'rounded';
  fallbackIcon?: React.ReactNode;
  fallbackText?: string;
  className?: string;
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down';
  objectPosition?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  autoCrop?: boolean;
  quality?: 'low' | 'medium' | 'high';
}

const sizeClasses = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24',
  '2xl': 'w-32 h-32',
};

const shapeClasses = {
  circle: 'rounded-full',
  square: 'rounded-none',
  rounded: 'rounded-lg',
};

const objectFitClasses = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  'scale-down': 'object-scale-down',
};

const objectPositionClasses = {
  center: 'object-center',
  top: 'object-top',
  bottom: 'object-bottom',
  left: 'object-left',
  right: 'object-right',
  'top-left': 'object-top-left',
  'top-right': 'object-top-right',
  'bottom-left': 'object-bottom-left',
  'bottom-right': 'object-bottom-right',
};

export default function Avatar({
  photo,
  size = 'md',
  shape = 'circle',
  fallbackIcon,
  fallbackText,
  className = '',
  alt = 'Avatar',
  objectFit = 'cover',
  objectPosition = 'center',
  autoCrop = true,
  quality = 'medium',
}: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const photoUrl = React.useMemo(() => {
    if (!photo) return null;
    
    if (typeof photo === 'string') {
      return getPhotoUrl(photo);
    }
    
    return getPhotoUrlFromObject(photo);
  }, [photo]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const baseClasses = `${sizeClasses[size]} ${shapeClasses[shape]} bg-gray-200 flex items-center justify-center overflow-hidden ${className}`;
  
  // Enhanced image classes with better auto-sizing
  const imageClasses = [
    'w-full h-full',
    objectFitClasses[objectFit],
    objectPositionClasses[objectPosition],
    'transition-all duration-200 ease-in-out',
    autoCrop ? 'transform scale-105' : '', // Slight scale for better cropping
    isLoading ? 'opacity-0' : 'opacity-100',
  ].filter(Boolean).join(' ');

  // Show fallback if no photo URL, image failed to load, or still loading
  if (!photoUrl || imageError) {
    return (
      <div className={`${baseClasses} bg-gray-100 text-slate-400`}>
        {fallbackIcon || (
          <div className="text-center">
            {fallbackText ? (
              <span className="text-xs font-medium uppercase">
                {fallbackText.slice(0, 2)}
              </span>
            ) : (
              <svg
                className="w-1/2 h-1/2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${baseClasses} relative`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-inherit" />
      )}
      <img
        src={photoUrl}
        alt={alt}
        className={imageClasses}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
} 